import React, { useEffect, useState } from "react";
import { Box, Flex, Input } from "theme-ui";
import moment from "moment-timezone";

import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { copyText } from "./GeneratedProposals";
import { checkMailboxAccess, createServiceToken, listServiceTokens, revokeServiceToken, type ServiceToken } from "api/MaintenanceOpportunities";

/**
 * Mission Control access (2026-10-19): a token that acts as YOU on the
 * opportunity-proposal endpoints only - list opportunities, suggest an
 * owner, generate proposals, record a send. It cannot confirm owners, mint
 * tokens, or email anything. Shown once; revoke any time.
 */
const txt = { fontFamily: "Poppins", fontSize: "12px", color: "base_500" };

const ServiceTokens: React.FC = () => {
  const toast = useToast();
  const [rows, setRows] = useState<ServiceToken[] | null>(null);
  const [name, setName] = useState("Mission Control");
  const [fresh, setFresh] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mailbox, setMailbox] = useState<{ ok: boolean; text: string } | null>(null);
  const checkMailbox = async () => {
    setMailbox({ ok: true, text: "Checking…" });
    const r: any = await checkMailboxAccess();
    if (r?.error && r.canRead === undefined) return setMailbox({ ok: false, text: r.error });
    setMailbox(r.canRead
      ? { ok: true, text: `Can read ${r.mailbox}: replies to proposals you send can be tracked.` }
      : { ok: false, text: `Cannot read ${r.mailbox}: ${r.error ?? "refused"}` });
  };

  const load = () => listServiceTokens().then((r: any) => setRows(r?.error ? [] : r));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    const res: any = await createServiceToken(name);
    setBusy(false);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    setFresh(res.token);
    load();
  };
  const revoke = async (id: string) => {
    const res: any = await revokeServiceToken(id);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    toast.add("Revoked.", "var(--theme-ui-colors-success)");
    load();
  };

  const live = (rows ?? []).filter((r) => !r.revokedAt);
  return (
    <Box sx={{ marginTop: "22px", paddingTop: "18px", borderTop: "1px solid #EEF0F2" }}>
      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, color: "base_800" }}>Mission Control access</Box>
      <Box sx={{ ...txt, marginBottom: "10px" }}>
        A token that acts as you on opportunity proposals only: read opportunities, suggest owners, generate proposals, record sends. It cannot confirm an owner or send email.
      </Box>
      {fresh && (
        <Box sx={{ padding: "10px 12px", background: "#FFF8E1", borderRadius: "6px", marginBottom: "10px", fontFamily: "Poppins", fontSize: "12px", color: "#7A5A00" }}>
          Copy this now - it is not shown again.
          <Flex sx={{ gap: "8px", alignItems: "center", marginTop: "6px" }}>
            <code style={{ wordBreak: "break-all", flex: 1 }}>{fresh}</code>
            <BasicButton type="button" keepText onClick={() => copyText(fresh, toast, "Token copied")}>Copy</BasicButton>
            <BasicButton outline scheme="secondary" type="button" keepText onClick={() => setFresh(null)}>Done</BasicButton>
          </Flex>
        </Box>
      )}
      {live.map((t) => (
        <Flex key={t.id} sx={{ alignItems: "center", gap: "10px", padding: "6px 0", fontFamily: "Poppins", fontSize: "12px" }}>
          <span style={{ fontWeight: 600 }}>{t.name}</span>
          <code>{t.tokenPrefix}…</code>
          <span style={{ color: "#888", flex: 1 }}>{t.lastUsedAt ? `used ${moment.utc(t.lastUsedAt).fromNow()}` : "never used"}</span>
          <BasicButton outline scheme="danger" type="button" keepText onClick={() => revoke(t.id)}>Revoke</BasicButton>
        </Flex>
      ))}
      <Flex sx={{ gap: "8px", alignItems: "center", marginTop: "8px", flexWrap: "wrap" }}>
        <BasicButton outline scheme="secondary" type="button" keepText onClick={checkMailbox}>Check mailbox access</BasicButton>
        {mailbox && <span style={{ fontFamily: "Poppins", fontSize: 12, color: mailbox.ok ? "#0F6E56" : "#A60000" }}>{mailbox.text}</span>}
      </Flex>
      <Flex sx={{ gap: "8px", alignItems: "center", marginTop: "8px" }}>
        <Input value={name} onChange={(e) => setName(e.target.value)} sx={{ fontFamily: "Poppins", fontSize: "13px", borderColor: "#EBEBEB", borderRadius: "6px", maxWidth: "240px" }} />
        <BasicButton type="button" keepText disabled={busy} onClick={create}>{busy ? "Creating…" : "Create token"}</BasicButton>
      </Flex>
    </Box>
  );
};

export default ServiceTokens;
